import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Youtube, Linkedin, Shield } from "lucide-react";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function Footer() {
  const { formatted_phone, phone_link, client_email, facebook_url, instagram_url, youtube_url, linkedin_url, tiktok_url, nextdoor_url, yelp_url, google_business_url } = useSiteConfig();
  
  // Key service areas organized by county
  const serviceAreas = [
    { name: "Fort Worth", slug: "fort-worth-tarrant-junk-removal" },
    { name: "Denton", slug: "denton-denton-junk-removal" },
    { name: "Keller", slug: "keller-tarrant-junk-removal" },
    { name: "Weatherford", slug: "weatherford-parker-junk-removal" },
    { name: "Decatur", slug: "decatur-wise-junk-removal" },
    { name: "Flower Mound", slug: "flower-mound-denton-junk-removal" },
    { name: "Southlake", slug: "southlake-tarrant-junk-removal" },
    { name: "Frisco", slug: "frisco-denton-junk-removal" },
  ];
  
  return (
    <footer data-testid="main-footer" className="bg-[#1a1a1a] text-white noise-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img 
                src="/images/overhaul-ops-logo-128.png" 
                alt="Overhaul Ops LLC - Junk Removal Services DFW"
                className="h-16 w-auto object-contain"
                width="64"
                height="64"
                loading="lazy"
              />
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Firefighter owned and operated junk removal company serving the DFW metroplex. 
              Professional, insured, and committed to excellence.
            </p>
            <div className="flex items-center gap-2 text-[#FFD700]">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium uppercase tracking-wider">Fully Insured</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 
              className="text-lg font-bold uppercase tracking-wider mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { name: "Home", path: "/" },
                { name: "About Us", path: "/about" },
                { name: "Services", path: "/services" },
                { name: "Areas Served", path: "/locations" },
                { name: "Gallery", path: "/gallery" },
                { name: "Testimonials", path: "/testimonials" },
                { name: "Discounts", path: "/community-appreciation-discount" },
                { name: "Clutter News", path: "/blog" },
                { name: "Contact", path: "/contact" },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-[#b51a00] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 
              className="text-lg font-bold uppercase tracking-wider mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Our Services
            </h4>
            <ul className="space-y-3 text-gray-400">
              <li>Residential Junk Removal</li>
              <li>Furniture & Appliance Removal</li>
              <li>Estate Cleanouts</li>
              <li>Hoarder Cleanouts</li>
              <li>Commercial Cleanouts</li>
              <li>Construction Debris Removal</li>
              <li>Light Demolition</li>
              <li>Material Delivery</li>
            </ul>
          </div>

          {/* Areas We Serve */}
          <div>
            <h4 
              className="text-lg font-bold uppercase tracking-wider mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Areas We Serve
            </h4>
            <ul className="space-y-3">
              {serviceAreas.map((area) => (
                <li key={area.slug}>
                  <Link 
                    to={`/locations/${area.slug}`}
                    className="text-gray-400 hover:text-[#b51a00] transition-colors"
                  >
                    {area.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to="/locations"
                  className="text-[#b51a00] hover:text-white transition-colors font-medium"
                >
                  View All Areas →
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 
              className="text-lg font-bold uppercase tracking-wider mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={phone_link} 
                  data-testid="footer-phone"
                  className="flex items-start gap-3 text-gray-400 hover:text-[#b51a00] transition-colors"
                >
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{formatted_phone}</span>
                </a>
              </li>
              <li>
                <Link 
                  to="/contact"
                  data-testid="footer-contact-link"
                  className="flex items-start gap-3 text-gray-400 hover:text-[#b51a00] transition-colors"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Send us a message</span>
                </Link>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>Serving DFW Metroplex<br/>Denton • Tarrant • Parker • Wise</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>7AM - 6PM<br/>7 Days a Week</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Overhaul Ops LLC. All rights reserved.
            </p>
            <a 
              href="https://web-techservices.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-400 text-sm"
            >
              Web design and hosting - Web-Techservices.com - AI
            </a>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-gray-400 text-sm">
              Privacy Policy
            </Link>
            <Link to="/sms-policy" className="text-gray-500 hover:text-gray-400 text-sm">
              SMS Policy
            </Link>
            <Link to="/sitemap" className="text-gray-500 hover:text-gray-400 text-sm">
              Sitemap
            </Link>
            <Link to="/admin/login" data-testid="footer-admin-link" className="text-gray-600 hover:text-gray-500 text-xs">
              Admin
            </Link>
            {/* Social Media Icons */}
            {facebook_url && (
              <a 
                href={facebook_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="text-gray-400 hover:text-[#b51a00] transition-colors"
                title="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            )}
            {instagram_url && (
              <a 
                href={instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="text-gray-400 hover:text-[#b51a00] transition-colors"
                title="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            )}
            {youtube_url && (
              <a 
                href={youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-youtube"
                className="text-gray-400 hover:text-[#b51a00] transition-colors"
                title="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            )}
            {linkedin_url && (
              <a 
                href={linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="text-gray-400 hover:text-[#b51a00] transition-colors"
                title="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            )}
            {tiktok_url && (
              <a 
                href={tiktok_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tiktok"
                className="text-gray-400 hover:text-[#b51a00] transition-colors"
                title="TikTok"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
              </a>
            )}
            {nextdoor_url && (
              <a 
                href={nextdoor_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-nextdoor"
                className="text-gray-400 hover:text-[#b51a00] transition-colors"
                title="Nextdoor"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm4 0h-2v-6h2v6zm-2-8c-.55 0-1-.45-1-1V6h2v2c0 .55-.45 1-1 1z"/>
                </svg>
              </a>
            )}
            {yelp_url && (
              <a 
                href={yelp_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-yelp"
                className="text-gray-400 hover:text-[#b51a00] transition-colors"
                title="Yelp"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.14 11.18l2.14 2.14c.39.39.39 1.02 0 1.41l-2.14 2.14c-.39.39-1.02.39-1.41 0l-2.14-2.14c-.39-.39-.39-1.02 0-1.41l2.14-2.14c.39-.39 1.02-.39 1.41 0zM10.59 6.41c.78-.78 2.05-.78 2.83 0l4.24 4.24c.78.78.78 2.05 0 2.83l-4.24 4.24c-.78.78-2.05.78-2.83 0l-4.24-4.24c-.78-.78-.78-2.05 0-2.83l4.24-4.24z"/>
                </svg>
              </a>
            )}
            {google_business_url && (
              <a 
                href={google_business_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-google"
                className="text-gray-400 hover:text-[#b51a00] transition-colors"
                title="Google Business"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
                </svg>
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
